/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import formatters from './formatters';
import { LIST_BLOCK_TYPES } from './constants';

function getSelectedBlocks(state) {
  const content = state.getCurrentContent();
  const selection = state.getSelection();
  const startKey = selection.getStartKey();
  let endKey = selection.getEndKey();

  if (startKey !== endKey && selection.getEndOffset() === 0) {
    endKey = content.getKeyBefore(endKey) || endKey;
  }

  const blocks = [];
  let key = startKey;
  while (key) {
    const block = content.getBlockForKey(key);
    if (block) {
      blocks.push(block);
    }
    if (key === endKey) {
      break;
    }
    key = content.getKeyAfter(key);
  }

  return blocks;
}

function getListStateInfo(state) {
  const blocks = getSelectedBlocks(state);
  const blockTypes = new Set(blocks.map((block) => block.getType()));
  const depths = new Set(blocks.map((block) => block.getDepth()));
  const hasList = blocks.some((block) =>
    LIST_BLOCK_TYPES.includes(block.getType())
  );

  return {
    hasList,
    isListMixed: blockTypes.size > 1,
    isUnorderedList:
      blockTypes.size === 1 && blockTypes.has('unordered-list-item'),
    isOrderedList: blockTypes.size === 1 && blockTypes.has('ordered-list-item'),
    listDepth: depths.size === 1 ? blocks[0]?.getDepth() ?? 0 : null,
  };
}

function getStateInfo(state, context = {}) {
  const stateInfo = formatters.reduce(
    (aggr, { getters }) => ({
      ...aggr,
      ...Object.fromEntries(
        Object.entries(getters).map(([key, getter]) => [
          key,
          getter(state, context),
        ])
      ),
    }),
    {}
  );
  return {
    ...stateInfo,
    ...getListStateInfo(state),
  };
}

export default getStateInfo;
